import type { SubmitFeedbackPayload } from "../components/feedback/feedback.types";
import { HotelBrandingData } from "../pages/booking-engine/types";
import type {
  DailyAllocation,
  EmailTemplate,
  QuoteMetrics,
} from "../pages/groups/types";
import type {
  AgiConnectRequest,
  AgiConnectResponse,
  AgiStatus,
  MarkRespondedResponse,
  PublishResponse,
  Channel,
  Review,
  ReviewDashboardStats,
  ReviewFilters,
  ReviewPeriod,
  ReviewsChatRequest,
  ReviewsChatResponse,
  ReviewsResponse,
  ReviewThemes,
  SuggestionRequest,
  SuggestionResponse,
  SyncResponse,
} from "../types/reviews";
import type { AuthResponse } from "../types/linked-accounts";
import apiClient from "./client";
import type {
  Phase1StatusResponse,
  VerificationStatusOrNull,
  VerificationStatusResponse,
} from "./api-types";
import {
  RatePlan,
  UpdateRatePlanData,
} from "./hooks/use-booking-engine-settings";

export type PlanPriceInterval = "day" | "week" | "month" | "year";

export interface PlanPrice {
  amount: number;
  currency: string;
  interval: PlanPriceInterval | null;
}

export interface Plan {
  id: string;
  name: string;
  description: string;
  commissionRate: number | null;
  price: PlanPrice | null;
}

export interface PlansResponse {
  plans: Plan[];
}

// All-in-one (U) onboarding: the backend now accepts an array of per-product
// plan selections alongside the legacy flat planId/promoCode fields.
export interface PaymentSelection {
  product: "pricing" | "marketing";
  planId: string;
  promoCode?: string;
}

/**
 * A user's country-specific invoicing registration (today: Mexican SAT data),
 * as stored by the backend. `null` fields mean "not provided yet" — the
 * invoice simply omits them.
 */
export interface FiscalDetails {
  countryCode: string;
  legalName: string | null;
  rfc: string | null;
  fiscalAddress: string | null;
  postalCode: string | null;
  updatedAt?: string | null;
}

/**
 * Full-replace payload for PUT /users/:id/fiscal-details: omitted (or empty)
 * fields are stored as NULL, so sending the current form state each save both
 * updates and clears in one shape.
 */
export interface UpsertFiscalDetailsPayload {
  countryCode: "MX";
  legalName?: string;
  rfc?: string;
  fiscalAddress?: string;
  postalCode?: string;
}

// Hard ceiling for the onboarding voice-agent's read/escalate calls so a hung
// request can't freeze the in-call client tools.
const ONBOARDING_AGENT_TIMEOUT_MS = 15_000;

// Tag-verification calls are short server round-trips (the verification work
// itself runs in the background server-side). A hung socket would otherwise
// pin the verify screen's footer — Back is disabled while a skip is in
// flight — so every call gets a hard ceiling; the screenshot upload carries a
// multi-MB base64 data URL and gets a larger one.
const TAG_VERIFICATION_TIMEOUT_MS = 15_000;
const TAG_VERIFICATION_UPLOAD_TIMEOUT_MS = 60_000;

export const api = {
  login: (data: any) => {
    return apiClient.post("/auth/login", data);
  },
  register: (data: {
    email: string;
    password: string;
    firstName: string;
    lastName: string;
    userType?: "U" | "MarketingOnly" | "A";
    emailVerificationToken: string;
  }) => {
    return apiClient.post("/auth/register", data);
  },
  sendSignupOtp: (email: string) =>
    apiClient.post<{ codeLength: number }>("/auth/otp/send", { email }),
  verifySignupOtp: (email: string, code: string) =>
    apiClient.post<{ verificationToken: string }>("/auth/otp/verify", {
      email,
      code,
    }),

  // Marketing onboarding — Stripe payment flow
  setupIntent: (userId: string) =>
    apiClient.post<{ clientSecret: string; setupIntentId: string }>(
      `/users/${userId}/setup-intent`,
      {},
    ),
  confirmPaymentMethod: (
    userId: string,
    setupIntentId: string,
    promoCode?: string,
    planId?: string,
    selections?: PaymentSelection[],
  ) =>
    apiClient.post<{ ok: true }>(`/users/${userId}/confirm-payment-method`, {
      setupIntentId,
      ...(promoCode ? { promoCode } : {}),
      ...(planId ? { planId } : {}),
      ...(selections && selections.length > 0 ? { selections } : {}),
    }),
  resolvePromoCode: (code: string, scope?: "pricing" | "marketing") =>
    apiClient.get<{ valid: false } | { valid: true; commissionRate: number }>(
      `/promo-codes/${encodeURIComponent(code)}/resolve`,
      { params: scope ? { scope } : undefined },
    ),
  getPlans: (params: { marketing?: boolean; pricing?: boolean } = {}) =>
    apiClient.get<PlansResponse>("/plans", {
      params: params.marketing
        ? { marketing: "true" }
        : params.pricing
          ? { pricing: "true" }
          : undefined,
    }),

  forgotPassword: (data: any) => {
    return apiClient.post("/auth/forgot-password", data);
  },
  resetPassword: (data: any) => {
    return apiClient.post("/auth/reset-password", data);
  },
  ssoEmailMarketing: (hotelId?: string) => {
    return apiClient
      .post("/auth/sso/email-marketing", { hotelId })
      .then((res) => res.data as { redirectUrl: string });
  },
  ssoSocialMarketing: (hotelId?: string) => {
    return apiClient
      .post("/auth/sso/social-marketing", { hotelId })
      .then((res) => res.data as { redirectUrl: string });
  },
  ssoWebsiteBuilder: (hotelId?: string) => {
    return apiClient
      .post("/auth/sso/website-builder", { hotelId })
      .then((res) => res.data as { redirectUrl: string });
  },
  /** Rehydrate the session blob for the current token — the `/auth/login`
   *  shape minus a fresh `accessToken`. Lets an entry point that already holds
   *  a token (admin "Open App" auto-login) populate `linkedAccounts` &
   *  `currentHotel` without minting a longer-lived session. */
  getSession: () => {
    return apiClient
      .get("/auth/session")
      .then((res) => res.data as Omit<AuthResponse, "accessToken">);
  },
  switchProperty: (targetUserId: string) => {
    return apiClient.post("/auth/switch-property", { targetUserId });
  },
  addProperty: (hotelName: string) => {
    return apiClient.post("/auth/add-property", { hotelName });
  },
  cancelAddProperty: (originUserId: string) => {
    return apiClient.post("/auth/cancel-add-property", { originUserId });
  },
  getUserProfile: (id: string) => {
    return apiClient.get(`/users/${id}`).then((res) => res.data);
  },
  patchPassword: (id: string, data: any) => {
    return apiClient.patch(`/users/${id}/password`, data);
  },
  putUserProfile: (id: string, data: any) => {
    return apiClient.put(`/users/${id}`, data);
  },
  getSignedUrl: (
    id: string,
    params: { uploadType: string; mimeType: string; fileName?: string },
  ) => {
    return apiClient
      .get(`/users/${id}/images/signed-url`, { params })
      .then((res) => res.data);
  },
  getUserSubscription: (id: string) => {
    return apiClient.get(`/users/${id}/subscriptions`).then((res) => res.data);
  },
  // Mexican invoicing details (Hotel Info onboarding step, MX properties only).
  fiscalDetails: {
    get: (userId: string): Promise<{ fiscalDetails: FiscalDetails | null }> =>
      apiClient.get(`/users/${userId}/fiscal-details`).then((res) => res.data),
    upsert: (
      userId: string,
      payload: UpsertFiscalDetailsPayload,
    ): Promise<{ fiscalDetails: FiscalDetails }> =>
      apiClient
        .put(`/users/${userId}/fiscal-details`, payload)
        .then((res) => res.data),
  },
  setActiveStatus: (id: string) => {
    return apiClient.post(`/users/${id}/active`);
  },
  getStripe: (id: string, tier: "ST" | "PR" | "MX") => {
    return apiClient
      .get(`/users/${id}/stripe-connect-url`, {
        params: {
          redirectUrl: window.location.origin + "/onboarding",
          planType: "M",
          tier: tier,
        },
      })
      .then((res) => res.data);
  },
  clickStripe: (id: string) => {
    return apiClient.post(`/users/${id}/stripe/clicked/continue/checkout`);
  },
  patchStripe: (
    id: string,
    tier: "ST" | "PR" | "MX",
    subscriptionId: string,
  ) => {
    return apiClient.patch(`/users/${id}/subscriptions/${subscriptionId}`, {
      tier: tier,
      planType: "M",
    });
  },
  customerStripePortal: (id: string) => {
    return apiClient
      .get(`/users/${id}/stripe-customer-portal-url`, {
        params: {
          // redirectUrl: "https://dev.app.stealthseller.co/dashboard",
          // redirectUrl: "http://localhost:3002/dashboard",
          // dynamicRedirectUrl
          redirectUrl: window.location.origin + "/dashboard",
        },
      })
      .then((res) => res.data);
  },
  cancelSubscription: (id: string, subscriptionId: string) => {
    return apiClient
      .delete(`/users/${id}/subscriptions/${subscriptionId}`)
      .then((res) => res.data);
  },
  getUser: (id: string) => {
    return apiClient.get(`/users/${id}`).then((res) => res.data);
  },
  initiateEmailUpdate: (userId: string, email: string) => {
    return apiClient.post(`/users/${userId}/email/update/initiate`, {
      email,
    });
  },
  updateEmail: (userId: string, otp: string) => {
    return apiClient.post(`/users/${userId}/email/update`, {
      otp,
    });
  },
  deleteUser: (userId: string) => {
    return apiClient.delete(`/users/${userId}`);
  },
  getGraph: (sellerId: string, productId: string) => {
    return apiClient.get(`/sellers/${sellerId}/products/${productId}/graph`, {
      responseType: "blob",
    });
  },
  updateUserActivity: (id: string) => {
    return apiClient.post(`/users/${id}/active`);
  },
  saveOnboarding: (userId: string, data: any) => {
    delete data.userPms;
    return apiClient.post(`/users/${userId}/onboarding`, data);
  },
  // Onboarding pre-fill detection (marketing flow ONLY — never call for
  // all-in-one; their PMS connection proves the answer a guess would only
  // approximate). Contract: autumn-backend/docs/onboarding-prefill-detection.md.
  // Always 200 with empty suggestions on any detection failure; a
  // browserPass of "pending" invites a bounded re-POST of the SAME URL.
  detectSitePlatforms: (websiteUrl: string) => {
    return apiClient.post(`/marketing-agent/detect-site-platforms`, {
      websiteUrl,
    });
  },
  // Root-language check, fired on the SAME settle event (BOTH flows, unlike
  // detectSitePlatforms). Contract:
  // autumn-backend/docs/onboarding-language-websites.md - Root-language
  // warning. Always 200; `warning` is ready-to-render copy or null.
  checkRootLanguage: (websiteUrl: string) => {
    return apiClient.post(`/marketing-agent/check-root-language`, {
      websiteUrl,
    });
  },
  addSeason: (userId: string, data: any) => {
    return apiClient.post(`/users/${userId}/add/season`, data);
  },
  deleteSeason: (userId: string, seasonId: string) => {
    return apiClient.delete(`/users/${userId}/delete/season`, {
      data: { id: seasonId },
    });
  },
  completeOnboarding: (userId: string) => {
    return apiClient.post(`/users/${userId}/onboarding/complete`);
  },
  goLive: (userId: string) => {
    return apiClient.post(`/users/${userId}/go-live`);
  },
  activateV2Pricing: (userId: string) => {
    return apiClient.post(`/users/${userId}/activate-v2-pricing`);
  },
  getOnboarding: (userId: string) => {
    return apiClient.get(`/users/${userId}/onboarding`).then((res) => res.data);
  },
  deleteOnboarding: (userId: string) => {
    return apiClient.delete(`/users/${userId}/onboarding`);
  },
  getPhase1Status: (userId: string): Promise<Phase1StatusResponse> => {
    return apiClient
      .get(`/sem-provisioning/phase-1-status/${userId}`)
      .then((res) => res.data);
  },
  tagVerificationRun: (userId: string): Promise<{ runId: string }> => {
    return apiClient
      .post(`/tag-verification/${userId}/run`, undefined, {
        timeout: TAG_VERIFICATION_TIMEOUT_MS,
      })
      .then((res) => res.data);
  },
  tagVerificationStatus: (
    userId: string,
    runId?: string,
  ): Promise<VerificationStatusOrNull> => {
    // Calling without `runId` is the documented "preflight" usage: the BE
    // returns the user's current snapshot (or null when none exists),
    // regardless of which run produced it. axios omits undefined query
    // params automatically, so no extra branching is needed.
    //
    // BE wraps the response in `{ snapshot: ... }` so a "no snapshot"
    // result lands as `{ snapshot: null }` instead of an empty body —
    // bare `null` returns get stripped to zero bytes by NestJS's response
    // sender, which axios parses as `''` rather than `null`, breaking the
    // FE's preflight `=== null` check. Unwrap here so callers see the
    // historical `VerificationStatusOrNull` contract. The `=== ""` branch
    // tolerates a legacy zero-byte response from an older BE deployment.
    return apiClient
      .get<{ snapshot: VerificationStatusOrNull } | "">(
        `/tag-verification/${userId}/status`,
        {
          params: runId ? { runId } : undefined,
          timeout: TAG_VERIFICATION_TIMEOUT_MS,
        },
      )
      .then((res) => {
        const envelope = res.data;
        if (envelope === "" || envelope == null) return null;
        return (
          (envelope as { snapshot: VerificationStatusOrNull }).snapshot ?? null
        );
      });
  },
  tagVerificationSkip: (
    userId: string,
  ): Promise<VerificationStatusResponse> => {
    return apiClient
      .post(`/tag-verification/${userId}/skip`, undefined, {
        timeout: TAG_VERIFICATION_TIMEOUT_MS,
      })
      .then((res) => res.data);
  },
  tagVerificationUploadScreenshot: (
    userId: string,
    screenshot: string,
  ): Promise<{ ok: true }> => {
    return apiClient
      .post(
        `/tag-verification/${userId}/screenshot`,
        { screenshot },
        { timeout: TAG_VERIFICATION_UPLOAD_TIMEOUT_MS },
      )
      .then((res) => res.data);
  },
  tagVerificationDeleteScreenshot: (
    userId: string,
  ): Promise<{ ok: true }> => {
    return apiClient
      .delete(`/tag-verification/${userId}/screenshot`, {
        timeout: TAG_VERIFICATION_TIMEOUT_MS,
      })
      .then((res) => res.data);
  },
  connectPMS: (
    userId: string,
    data: {
      accessToken: string;
      pms: string;
      propertyID?: string;
    },
  ) => {
    const url = `/users/${userId}/pms/connect`;
    return apiClient.post(url, data);
  },
  autoConnectResNexusPMS: (
    userId: string,
    data: {
      action: "connect" | "skip";
      comment?: string;
    },
  ): Promise<{
    connected: boolean;
    reason?: string;
    businessId?: string;
    businessName?: string;
  }> => {
    const url = `/users/${userId}/pms/resnexus/auto-connect`;
    return apiClient.post(url, data).then((res) => res.data);
  },
  syncRatesToPms: (
    userId: string,
    data: {
      type: string;
    },
  ) => {
    const url = `/users/${userId}/pms/sync-rates`;
    return apiClient.post(url, data);
  },
  syncBookings: (
    userId: string,
    data: {
      startDate: string;
      endDate: string;
    },
  ) => {
    const url = `/users/${userId}/pms/sync/bookings/immediate`;
    return apiClient.post(url, data);
  },

  getFeatureFlags: (userId: string) => {
    return apiClient.get<{
      enableInsights: boolean;
      enableMarketingKpiCard: boolean;
      enableDashboardInsights: boolean;
      enableDashboardKpiCard: boolean;
      enableEmailDigest: boolean;
      enableMarketTrend: boolean;
    }>(`/users/${userId}/feature-flags`);
  },

  getPricingFeatureFlags: (userId: string) => {
    return apiClient.get<{
      enableBookingCurveOffset: boolean;
      enablePaceAdjustments: boolean;
      enableDynamicDifferentials: boolean;
      enableShadowMode: boolean;
      enableNotifications: boolean;
      enableGuestProtection: boolean;
      guestProtectionThresholdPct: number | null;
    }>(`/users/${userId}/pricing-config/feature-flags`);
  },

  updatePricingFeatureFlag: (
    userId: string,
    flag: string,
    enabled: boolean,
  ) => {
    return apiClient.patch<{ [key: string]: boolean }>(
      `/users/${userId}/pricing-config/feature-flags`,
      { flag, enabled },
    );
  },

  updateGuestProtectionSettings: (
    userId: string,
    enabled: boolean,
    thresholdPct?: number | null,
  ) => {
    return apiClient.patch<{
      enableGuestProtection: boolean;
      guestProtectionThresholdPct: number | null;
    }>(`/users/${userId}/pricing-config/guest-protection`, {
      enabled,
      thresholdPct,
    });
  },

  syncRatesGrouped: (
    userId: string,
    data: {
      startDate: string;
      endDate: string;
    },
  ) => {
    const url = `/users/${userId}/pms/sync/rates/grouped`;
    return apiClient.post(url, data);
  },
  syncRatesGeneral: (
    userId: string,
    data: {
      startDate: string;
      endDate: string;
    },
  ) => {
    const url = `/users/${userId}pms/sync/rates`;
    return apiClient.post(url, data);
  },
  syncPmsConfiguration: (userId: string) => {
    const url = `/users/${userId}/pms/sync/configuration`;
    return apiClient.post(url);
  },

  updateAllSeasons: (userId: string) => {
    const url = `/users/${userId}/update/all/seasons`;
    return apiClient.post(url);
  },
  updateSeason: (userId: string, data: any) => {
    const url = `/users/${userId}/update/season`;
    return apiClient.post(url, data);
  },

  getMetrics: (
    userId: string,
    data: {
      startDate: string;
      endDate: string;
      targetStartDate?: string;
      targetEndDate?: string;
      type: string;
      stly?: boolean;
      roomType?: string;
      source?: string;
      roomTypeId?: string[];
      includeComparison?: boolean;
    },
  ) => {
    let url = `/users/${userId}/metrics?startDate=${data.startDate}&endDate=${data.endDate}&type=${data.type}`;
    if (data.targetStartDate && data.targetEndDate) {
      url += `&targetStartDate=${data.targetStartDate}&targetEndDate=${data.targetEndDate}`;
    }
    if (data.stly) {
      url += `&isSTLY=${data.stly}`;
    }
    if (data.roomType) {
      url += `&roomType=${encodeURIComponent(data.roomType)}`;
    }
    if (data.source) {
      url += `&source=${encodeURIComponent(data.source)}`;
    }
    if (data.roomTypeId && data.roomTypeId.length > 0) {
      // Send each room type ID as a separate query parameter
      data.roomTypeId.forEach((id) => {
        url += `&roomTypeId=${encodeURIComponent(id)}`;
      });
    }
    if (data.includeComparison) {
      url += `&includeComparison=${data.includeComparison}`;
    }
    return apiClient.get(url).then((res) => res.data);
  },
  getDayMetrics: (
    userId: string,
    data: { type: string; date: string; isSTLY?: boolean },
  ) => {
    let url = `/users/${userId}/day-metrics?type=${data.type}&date=${data.date}`;
    if (data.isSTLY) {
      url += `&isSTLY=${data.isSTLY}`;
    }
    return apiClient.get(url).then((res) => res.data);
  },
  getCalendarMetrics: (
    userId: string,
    data: { month: string; year: string; view?: string },
  ) => {
    const params = new URLSearchParams({
      month: data.month,
      year: data.year,
      ...(data.view && { view: data.view }),
    });
    const url = `/users/${userId}/day-metrics/month?${params.toString()}`;
    return apiClient.get(url).then((res) => res.data);
  },
  getHotelData: (userId: string) => {
    const url = `/users/${userId}/hotel-data`;
    return apiClient.get(url).then((res) => res.data);
  },
  saveDayPrice: (userId: string, data: any) => {
    const url = `/users/${userId}/price`;
    return apiClient.post(url, data);
  },

  overrideFloorCeiling: (userId: string, data: any) => {
    const url = `/users/${userId}/override/floor-ceilings`;
    return apiClient.post(url, data);
  },
  overrideBulkFloorCeiling: (userId: string, data: any) => {
    const url = `/users/${userId}/override/bulk/floor-ceilings`;
    return apiClient.post(url, data);
  },
  removeAllFloorCeilings: (userId: string) => {
    const url = `/users/${userId}/override/remove-floor-ceilings`;
    return apiClient.post(url).then((res) => res.data);
  },
  removeFloorCeilingsByRoom: (userId: string, roomTypePmsId: string) => {
    const url = `/users/${userId}/override/remove-floor-ceilings/${roomTypePmsId}`;
    return apiClient.post(url).then((res) => res.data);
  },
  resetDayPrice: (userId: string, data: any) => {
    const url = `/users/${userId}/price/reset`;
    return apiClient.post(url, data);
  },
  getCalendarData: (userId: string, startDate: string, endDate: string) => {
    const url = `/users/${userId}/calendar-data?startDate=${startDate}&endDate=${endDate}`;
    return apiClient.get(url).then((res) => res.data);
  },
  getMessages: (userId: string) => {
    const url = `/conversations?userId=${userId}`;
    return apiClient.get(url).then((res) => res.data);
  },
  postMessage: (data: any) => {
    const url = `/conversations`;
    //console.log("POST MESSAGE", data);
    return apiClient.post(url, data, {
      responseType: "stream",
      adapter: "fetch",
    });
  },
  TopSources: (id: string) => {
    const url = `/users/${id}/analytics`;
    return apiClient.patch(url, {
      analyticsType: "TopReferralSources",
    });
  },
  getGoogleAuthUrl: () => {
    const url = `/auth/google-auth-link`;
    //console.log("GOOGLE AUTH URL", url);
    const response = apiClient.get(url);
    //console.log("GOOGLE AUTH RESPONSE", response);
    return response;
  },
  post: (url: string, data: any) => {
    return apiClient.post(url, data);
  },
  getMarketingAnalytics: (
    userId: string,
    data: { startDate: string; endDate: string },
  ) => {
    const url = `/users/${userId}/dashboard/googleAds`;
    return apiClient.get(url, { params: data }).then((res) => res.data);
  },
  getAnalytics: (
    userId: string,
    data: {
      startDate: string;
      endDate: string;
      analyticsType:
        | "YTDSummary"
        | "TopTrafficChannels"
        | "TopReferralSources"
        | "GoogleAdWordsPerformance"
        | "WebsiteTrafficByDevice"
        | "WebsiteTrafficByTime"
        | "VisitorDemographics"
        | "FeederMarkets"
        | "UserEngagement"
        | "PageViews"
        | "Events";
    },
  ) => {
    const url = `/users/${userId}/analytics`;
    return apiClient.post(url, data).then((res) => res.data);
  },
  postLog: (data: any) => {
    const url = `/users/log`;
    return apiClient.post(url, data);
  },
  getFloorCeiling: (userId: string, data: any) => {
    const url = `/users/${userId}/floor-ceilings`;
    return apiClient.post(url, data);
  },
  saveComps: (userId: string) => {
    const url = `/users/${userId}/updatecomps`; // Removed extra quote mark that was causing malformed URL
    //console.log("saving Comps", url);
    return apiClient.post(url);
  },
  getBookingEngineSettings: (userId: string) => {
    const url = `/users/${userId}/booking-engine/settings`;
    return apiClient.get(url).then((res) => res.data);
  },
  updateBookingEngineSettings: (userId: string, data: any) => {
    const url = `/users/${userId}/booking-engine/settings`;
    return apiClient.put(url, data);
  },
  uploadBookingEngineAsset: (
    userId: string,
    assetType: "logo" | "hero",
    file: File,
  ) => {
    const formData = new FormData();
    formData.append("file", file);
    formData.append("assetType", assetType);
    const url = `/users/${userId}/booking-engine/upload`;
    return apiClient.post(url, formData, {
      headers: {
        "Content-Type": "multipart/form-data",
      },
    });
  },
  getBookingEngineSignedUrl: (params: {
    uploadType: "HOTEL_LOGO" | "HOTEL_HERO_PHOTO";
    mimeType: string;
    fileName?: string;
  }) => {
    return apiClient
      .get("/booking-engine/images/signed-url", { params })
      .then((res) => res.data);
  },
  updateHotelBranding: (data: HotelBrandingData) => {
    return apiClient
      .patch("/booking-engine/hotel/branding", data)
      .then((res) => res.data);
  },
  getBookingEngineAnalytics: (params: {
    startDate: string;
    endDate: string;
  }) => {
    return apiClient
      .get("/booking-engine/analytics/funnel", { params })
      .then((res) => res.data);
  },
  getDepositSettings: () => {
    return apiClient
      .get("/booking-engine/hotel/deposit-settings")
      .then((res) => res.data);
  },
  getHotelBranding: () => {
    return apiClient
      .get("/booking-engine/hotel/branding")
      .then((res) => res.data);
  },
  updateDepositSettings: (data: {
    depositType?: string;
    depositAmount?: number;
    depositPercentage?: number;
    includeTaxesAndFees?: boolean;
    collectAtBooking?: boolean;
    requireCreditCard?: boolean;
  }) => {
    return apiClient
      .patch("/booking-engine/hotel/deposit-settings", data)
      .then((res) => res.data);
  },

  // Rate Plans (Cloudbeds)
  getRatePlans: (): Promise<RatePlan[]> => {
    return apiClient
      .get<RatePlan[]>("/booking-engine/hotel/rate-plans")
      .then((res) => res.data);
  },
  updateRatePlan: (
    ratePlanId: string,
    data: UpdateRatePlanData,
  ): Promise<RatePlan> => {
    return apiClient
      .patch<RatePlan>(`/booking-engine/hotel/rate-plans/${ratePlanId}`, data)
      .then((res) => res.data);
  },

  // Booking Engine Go-Live
  getBookingEngineGoLive: (): Promise<{
    shouldGoLive: boolean;
    goLiveRequestedAt: string | null;
    showGoLiveButton: boolean;
  }> => {
    return apiClient
      .get("/booking-engine/hotel/go-live")
      .then((res) => res.data);
  },
  requestBookingEngineGoLive: (): Promise<{
    shouldGoLive: boolean;
    goLiveRequestedAt: string;
    showGoLiveButton: boolean;
  }> => {
    return apiClient
      .patch("/booking-engine/hotel/go-live")
      .then((res) => res.data);
  },

  // Document Management (RAG)
  documents: {
    list: () => apiClient.get("/documents/admin").then((res) => res.data),

    upload: (data: {
      userId: string;
      name: string;
      url: string;
      mimeType: string;
      ownerType: "S";
    }) => apiClient.post("/documents", data).then((res) => res.data),

    regenerate: (id: string) =>
      apiClient.post(`/documents/${id}/regenerate`).then((res) => res.data),

    download: (id: string) =>
      apiClient.get(`/documents/${id}/download`).then((res) => res.data),

    delete: (id: string) =>
      apiClient.delete(`/documents/${id}`).then((res) => res.data),

    getChunks: (id: string, page: number = 1, limit: number = 20) =>
      apiClient
        .get(`/documents/${id}/chunks`, { params: { page, limit } })
        .then((res) => res.data),
  },

  // Chat History / Conversations
  conversations: {
    /**
     * Get all conversations for the logged-in user
     * @returns Promise with conversations array
     */
    list: () => apiClient.get("/ai/conversations").then((res) => res.data),

    /**
     * Get all messages for a specific conversation
     * @param conversationId - The conversation ID
     * @returns Promise with conversation messages
     */
    getMessages: (conversationId: string) =>
      apiClient
        .get(`/ai/conversations/${conversationId}/messages`)
        .then((res) => res.data),

    updateWidgetState: (
      conversationId: string,
      widgetId: string,
      body: {
        messageId: string;
        actionType: string;
        status: 'applied' | 'adjusted' | 'dismissed';
        appliedPayload?: Record<string, any>;
      }
    ): Promise<void> =>
      apiClient
        .patch(`/ai/conversations/${conversationId}/widgets/${widgetId}/state`, body)
        .then((res) => res.data),

    /**
     * Delete a conversation (soft delete)
     * @param conversationId - The conversation ID
     * @returns Promise with success message
     */
    delete: (conversationId: string) =>
      apiClient
        .delete(`/ai/conversations/${conversationId}`)
        .then((res) => res.data),
  },

  billing: {
    getHistory: (
      userId: string,
      options?: {
        type?: "subscription" | "commission" | "fixed" | "hybrid";
        startDate?: string;
        endDate?: string;
        limit?: number;
        offset?: number;
      },
    ) => {
      const params: Record<string, string | number> = {};

      if (options?.type) {
        params.type = options.type;
      }
      if (options?.startDate) {
        params.startDate = options.startDate;
      }
      if (options?.endDate) {
        params.endDate = options.endDate;
      }
      if (options?.limit !== undefined) {
        params.limit = options.limit;
      }
      if (options?.offset !== undefined) {
        params.offset = options.offset;
      }

      return apiClient
        .get(`/users/${userId}/billing-history`, {
          params,
        })
        .then((res) => res.data);
    },

    getPaymentMethod: (userId: string) =>
      apiClient.get(`/users/${userId}/payment-method`).then((res) => res.data),

    // `returnUrl` is where Stripe sends the customer after they're done in the
    // portal. Defaults to Settings > Billing; MarketingOnly users can't reach
    // that route, so callers pass their own (see `useStripePortal`).
    getStripePortalUrl: (userId: string, returnUrl?: string) =>
      apiClient
        .get<{ url: string }>(`/users/${userId}/stripe-customer-portal-url`, {
          params: {
            redirectUrl:
              returnUrl ?? `${window.location.origin}/settings?tab=billing`,
          },
        })
        .then((res) => res.data),

    getBillingStatus: (userId: string, fromPortal?: boolean) =>
      apiClient.get(
        `/users/${userId}/billing-status${fromPortal ? "?fromPortal=true" : ""}`,
      ),

    getInvoiceHtml: (userId: string, invoiceId: string) =>
      apiClient
        .get<{ subject: string; html: string }>(
          `/users/${userId}/invoices/${encodeURIComponent(invoiceId)}`,
        )
        .then((res) => res.data),

    getInvoicePdf: (userId: string, invoiceId: string) =>
      apiClient
        .get<Blob>(
          `/users/${userId}/invoices/${encodeURIComponent(invoiceId)}/pdf`,
          { responseType: "blob" },
        )
        .then((res) => res.data),
  },

  // Google Ads Previews
  getAdPreviews: (userId: string) => {
    return apiClient
      .get(`/marketing-agent/ad-preview/${userId}`)
      .then((res) => res.data);
  },

  updateHotelProfile: (
    userId: string,
    hotelName: string,
    hotelWebsite?: string,
    googlePlaceId?: string,
  ) => {
    return apiClient
      .post(`/marketing-agent/hotel-profile/${userId}`, {
        hotelName,
        hotelWebsite,
        googlePlaceId,
      })
      .then((res) => res.data);
  },

  updateAdCopy: <TResponse = unknown>(
    userId: string,
    adId: string,
    patch: {
      headlines?: string[];
      descriptions?: string[];
      siteLinks?: Array<{ title: string; url: string }>;
    },
  ): Promise<TResponse> => {
    return apiClient
      .patch<TResponse>(
        `/marketing-agent/ad-preview/${userId}/copy/${adId}`,
        patch,
      )
      .then((res) => res.data);
  },

  // Onboarding Highlights (confirm discovered amenities + value props)
  getHighlights: (
    userId: string,
  ): Promise<{
    hotelName: string;
    discoveredHighlights: string[];
    confirmedHighlights: string[] | null;
    scrapedImages: string[];
    userProvidedPhotos: string[];
    hasResearch: boolean;
  }> => {
    return apiClient
      .get(`/marketing-agent/highlights/${userId}`)
      .then((res) => res.data);
  },

  saveConfirmedHighlights: (
    userId: string,
    highlights: string[],
  ): Promise<{
    hotelName: string;
    discoveredHighlights: string[];
    confirmedHighlights: string[] | null;
    scrapedImages: string[];
    userProvidedPhotos: string[];
    hasResearch: boolean;
  }> => {
    return apiClient
      .put(`/marketing-agent/highlights/${userId}`, { highlights })
      .then((res) => res.data);
  },

  addHotelPhotos: (
    userId: string,
    accessUrls: string[],
  ): Promise<{ userProvidedPhotos: string[] }> => {
    return apiClient
      .post(`/marketing-agent/highlights/${userId}/photos`, { accessUrls })
      .then((res) => res.data);
  },

  removeScrapedPhoto: (
    userId: string,
    imageUrl: string,
  ): Promise<{ scrapedImages: string[] }> => {
    return apiClient
      .delete(`/marketing-agent/highlights/${userId}/scraped-photos`, {
        data: { imageUrl },
      })
      .then((res) => res.data);
  },

  removeUserPhoto: (
    userId: string,
    imageUrl: string,
  ): Promise<{ userProvidedPhotos: string[] }> => {
    return apiClient
      .delete(`/marketing-agent/highlights/${userId}/photos`, {
        data: { imageUrl },
      })
      .then((res) => res.data);
  },

  confirmHighlights: (userId: string): Promise<{ imageCount: number }> => {
    return apiClient
      .post(`/marketing-agent/highlights/${userId}/confirm`)
      .then((res) => res.data);
  },

  markAdPreviewWaitingRoomSeen: (
    userId: string,
  ): Promise<{ recorded: boolean }> => {
    return apiClient
      .post(`/marketing-agent/ad-preview/${userId}/waiting-room-seen`)
      .then((res) => res.data);
  },

  // Weather
  getWeather: (start?: string, end?: string) => {
    const params = start && end ? `?start=${start}&end=${end}` : "";
    return apiClient.get(`/weather${params}`).then((res) => res.data.data);
  },

  // AI Reasoning Tab Data
  aiReasoning: {
    /**
     * Get personalized data for AI Reasoning tab
     * Returns competitors, room types, PMS, OTAs, active season, pricing
     */
    getData: () => apiClient.get("/ai/reasoning-data").then((res) => res.data),
  },

  groupQuotes: {
    generate: (input: {
      checkInDate: string;
      checkOutDate: string;
      roomCount: number;
      context: string;
      guestName?: string;
      guestEmail?: string;
    }) =>
      apiClient
        .post("/group-quotes/generate", input)
        .then((res) => res.data.data), // Backend wraps response in { success, data }

    list: (filters?: {
      status?: string;
      search?: string;
      startDate?: string;
      endDate?: string;
      limit?: number;
      offset?: number;
    }) =>
      apiClient
        .get("/group-quotes", { params: filters })
        .then((res) => res.data),

    get: (id: string) =>
      apiClient.get(`/group-quotes/${id}`).then((res) => res.data.data), // Backend wraps response in { success, data }

    save: (data: {
      checkInDate: string;
      checkOutDate: string;
      roomCount: number;
      context: string;
      guestName?: string;
      guestEmail?: string;
      allocation: DailyAllocation[];
      metrics: QuoteMetrics;
      emailDraft: {
        text: string;
        html: string;
        template?: EmailTemplate;
        subject?: string;
      };
      status?: string;
      expiresAt?: string;
      globalDiscountPercent?: number;
    }) => apiClient.post("/group-quotes", data).then((res) => res.data.data), // Backend wraps response in { success, data }

    /**
     * Update an existing quote
     * @param id - Quote ID
     * @param data - Fields to update
     * @returns Updated quote
     */
    update: (
      id: string,
      data: {
        status?: string;
        guestName?: string;
        guestEmail?: string;
        allocation?: DailyAllocation[];
        metrics?: QuoteMetrics;
        emailDraft?: {
          text: string;
          html: string;
          template?: EmailTemplate;
          subject?: string;
        };
        expiresAt?: string;
        sentAt?: string;
        respondedAt?: string;
        globalDiscountPercent?: number;
      },
    ) =>
      apiClient.put(`/group-quotes/${id}`, data).then((res) => res.data.data), // Backend wraps response in { success, data }

    delete: (id: string) =>
      apiClient.delete(`/group-quotes/${id}`).then((res) => res.data),

    getSettings: () =>
      apiClient.get("/group-quotes/settings").then((res) => res.data),

    updateSettings: (data: {
      hotelInfo?: {
        hotelName?: string;
        contactName?: string;
        contactEmail?: string;
        contactPhone?: string;
        tagline?: string;
        keyAmenities?: string[];
      };
      discountTiers?: Array<{ maxOccupancy: number; discountPercent: number }>;
      emailTemplate?: {
        greeting?: string;
        introduction?: string;
        closing?: string;
        signature?: string;
      };
    }) => apiClient.put("/group-quotes/settings", data).then((res) => res.data),

    regenerateTemplate: (data: {
      context: string;
      guestName?: string;
      checkInDate: string;
      checkOutDate: string;
      roomCount: number;
      currentTemplate: {
        greeting: string;
        introduction: string;
        closing: string;
        signature: string;
      };
    }) =>
      apiClient
        .post("/group-quotes/regenerate-template", data)
        .then((res) => res.data.data.emailDraft),

    parseInquiry: (emailText: string) =>
      apiClient
        .post("/group-quotes/parse-inquiry", { emailText })
        .then((res) => res.data.data),
  },

  reputation: {
    getReviews: (filters?: ReviewFilters): Promise<ReviewsResponse> =>
      apiClient
        .get("/reputation/reviews", { params: filters })
        .then((res) => res.data),

    getReview: (id: string): Promise<Review> =>
      apiClient.get(`/reputation/reviews/${id}`).then((res) => res.data),

    suggest: (
      id: string,
      body?: SuggestionRequest,
    ): Promise<SuggestionResponse> =>
      apiClient
        .post(`/reputation/reviews/${id}/suggest`, body)
        .then((res) => res.data),

    // Returns AUTO for Google (direct publish) or ASSISTED for OTAs (deep link)
    publish: (id: string, responseText: string): Promise<PublishResponse> =>
      apiClient
        .post(`/reputation/reviews/${id}/publish`, { responseText })
        .then((res) => res.data),

    // Called after user manually posts response on OTA extranet
    markResponded: (
      id: string,
      responseText: string,
    ): Promise<MarkRespondedResponse> =>
      apiClient
        .post(`/reputation/reviews/${id}/mark-responded`, { responseText })
        .then((res) => res.data),

    getStats: (period?: ReviewPeriod): Promise<ReviewDashboardStats> =>
      apiClient
        .get("/reputation/reviews/stats", {
          params: period ? { period } : undefined,
        })
        .then((res) => res.data),

    getThemes: (period?: ReviewPeriod, channel?: Channel): Promise<ReviewThemes> =>
      apiClient
        .get("/reputation/reviews/themes", {
          params: {
            ...(period && { period }),
            ...(channel && { channel }),
          },
        })
        .then((res) => res.data),

    sync: (): Promise<SyncResponse> =>
      apiClient.post("/reputation/sync").then((res) => res.data),

    getStatus: (): Promise<AgiStatus> =>
      apiClient.get("/reputation/status").then((res) => res.data),

    connect: (data: AgiConnectRequest): Promise<AgiConnectResponse> =>
      apiClient.post("/reputation/connect", data).then((res) => res.data),

    chat: (data: ReviewsChatRequest): Promise<ReviewsChatResponse> =>
      apiClient.post("/reputation/reviews/chat", data).then((res) => res.data),
  },

  pricingConfigAgent: {
    listConversations: (params?: { limit?: number; offset?: number }): Promise<{
      conversations: {
        chatId: string;
        preview: string;
        messageCount: number;
        createdAt: string;
        updatedAt: string;
      }[];
      total: number;
    }> =>
      apiClient
        .get("/ai/pricing-config-agent/conversations", { params: { limit: params?.limit ?? 20, offset: params?.offset ?? 0 } })
        .then((res) => res.data),

    getMessages: (chatId: string): Promise<{
      messages: { id: string; role: string; content: string; createdAt: string; parts?: any[] }[];
    }> =>
      apiClient
        .get(`/ai/pricing-config-agent/conversations/${chatId}/messages`)
        .then((res) => res.data),

    updateWidgetStatus: (chatId: string, messageId: string, partIndex: number, status: 'applied' | 'dismissed', widgetId?: string): Promise<void> =>
      apiClient
        .patch(`/ai/pricing-config-agent/conversations/${chatId}/widget-status`, { messageId, partIndex, status, ...(widgetId && { widgetId }) })
        .then((res) => res.data),

    delete: (chatId: string): Promise<void> =>
      apiClient
        .delete(`/ai/pricing-config-agent/conversations/${chatId}`)
        .then((res) => res.data),
  },

  pricingNotifications: {
    list: (params?: {
      dateFrom?: string;
      dateTo?: string;
      type?: string;
      roomType?: string;
      isRead?: boolean;
      isSignificant?: boolean;
      page?: number;
      limit?: number;
    }): Promise<any> =>
      apiClient
        .get("/pricing/notifications", { params })
        .then((res) => res.data),

    summary: (): Promise<{
      total: number;
      unread: number;
      pendingRecommendations: number;
      byType: Record<string, { total: number; unread: number }>;
    }> =>
      apiClient.get("/pricing/notifications/summary").then((res) => res.data),

    get: (id: string): Promise<any> =>
      apiClient.get(`/pricing/notifications/${id}`).then((res) => res.data),

    markRead: (id: string): Promise<any> =>
      apiClient
        .post(`/pricing/notifications/${id}/read`)
        .then((res) => res.data),

    markAllRead: (): Promise<any> =>
      apiClient.post("/pricing/notifications/read-all").then((res) => res.data),

    approve: (id: string, body?: { adjustedValue?: number }): Promise<any> =>
      apiClient
        .post(`/pricing/notifications/${id}/approve`, body ?? {})
        .then((res) => res.data),

    dismiss: (id: string): Promise<any> =>
      apiClient
        .post(`/pricing/notifications/${id}/dismiss`)
        .then((res) => res.data),

    volume: (params?: {
      dateFrom?: string;
      dateTo?: string;
    }): Promise<{
      totalDecisions: number;
      constrained: number;
      pushEvents: number;
    }> =>
      apiClient
        .get("/pricing/notifications/volume", { params })
        .then((res) => res.data),

    sparklines: (
      month: number,
      year: number,
    ): Promise<
      Record<
        string,
        {
          checkpoints: number[];
          tyOccupancy: (number | null)[];
          stlyOccupancy: number[];
        }
      >
    > =>
      apiClient
        .get(`/pricing/notifications/sparklines?month=${month}&year=${year}`)
        .then((res) => res.data),

    bookingCurve: (
      date: string,
      maxDaysOut?: number,
    ): Promise<{
      stayDate: string;
      stlyDate: string;
      actualDaysOut: number;
      totalRooms: number;
      checkpoints: number[];
      tyOccupancy: (number | null)[];
      stlyOccupancy: number[];
    }> =>
      apiClient
        .get("/pricing/notifications/booking-curve", {
          params: { date, ...(maxDaysOut && { maxDaysOut }) },
        })
        .then((res) => res.data),

    stream: (params?: {
      dateFrom?: string;
      dateTo?: string;
      eventType?: string;
      page?: number;
      limit?: number;
    }): Promise<any> =>
      apiClient
        .get('/pricing/notifications/stream', { params })
        .then((res) => res.data),
  },

  mlos: {
    getConfig: (userId: string): Promise<any> =>
      apiClient.get(`/mlos/${userId}/config`).then((res) => res.data),

    updateConfig: (userId: string, config: any): Promise<any> =>
      apiClient.put(`/mlos/${userId}/config`, config).then((res) => res.data),

    getRestrictions: (
      userId: string,
      params?: { startDate?: string; endDate?: string; roomTypeId?: string },
    ): Promise<any> =>
      apiClient
        .get(`/mlos/${userId}/restrictions`, { params })
        .then((res) => res.data),

    setManualRestriction: (
      userId: string,
      body: { date: string; minLos: number; roomTypeId?: string | null; autoRemoveOnInventoryMismatch?: boolean },
    ): Promise<any> =>
      apiClient
        .post(`/mlos/${userId}/restrictions/manual`, body)
        .then((res) => res.data),

    removeRestriction: (userId: string, date: string, roomTypeId?: string): Promise<any> =>
      apiClient
        .delete(`/mlos/${userId}/restrictions/${date}`, {
          params: roomTypeId ? { roomTypeId } : undefined,
        })
        .then((res) => res.data),

    getRoomTypes: (userId: string): Promise<any> =>
      apiClient.get(`/mlos/${userId}/room-types`).then((res) => res.data),

    getRestrictionsByDate: (userId: string, date: string): Promise<any> =>
      apiClient.get(`/mlos/${userId}/restrictions/by-date/${date}`).then((res) => res.data),

    triggerEvaluation: (
      userId: string,
      body?: { dates?: string[] },
    ): Promise<any> =>
      apiClient
        .post(`/mlos/${userId}/evaluate`, body ?? {})
        .then((res) => res.data),

    getPreview: (userId: string): Promise<any> =>
      apiClient.get(`/mlos/${userId}/preview`).then((res) => res.data),

    importRestrictions: (userId: string): Promise<any> =>
      apiClient.post(`/mlos/${userId}/import`).then((res) => res.data),

    getImportPreview: (userId: string): Promise<any> =>
      apiClient.get(`/mlos/${userId}/import/preview`).then((res) => res.data),

    getDecisionLog: (
      userId: string,
      params?: { date?: string; limit?: number; roomTypeId?: string },
    ): Promise<any> =>
      apiClient
        .get(`/mlos/${userId}/decisions`, { params })
        .then((res) => res.data),

    generatePreview: (userId: string): Promise<any> =>
      apiClient
        .post(`/mlos/${userId}/generate-preview`)
        .then((res) => res.data),

    activate: (userId: string): Promise<any> =>
      apiClient
        .post(`/mlos/${userId}/activate`)
        .then((res) => res.data),
  },

  pmsRevenueCategories: {
    discover: (
      userId: string,
    ): Promise<{
      pmsType: string;
      entries: Array<{
        sourceKind: "service";
        sourceId: string;
        sourceName: string;
        suggestedInternalCategory: "fnb" | "retail" | "other";
        currentInternalCategory: "fnb" | "retail" | "other" | null;
      }>;
    }> =>
      apiClient
        .get(`/users/${userId}/pms/revenue-categories/discover`)
        .then((res) => res.data),

    saveMapping: (
      userId: string,
      entries: Array<{
        sourceKind: "service";
        sourceId: string;
        sourceName: string;
        internalCategory: "fnb" | "retail" | "other";
      }>,
    ): Promise<{ saved: number; pmsType: string }> =>
      apiClient
        .put(`/users/${userId}/pms/revenue-categories/mapping`, { entries })
        .then((res) => res.data),
  },

  tasks: {
    list: (): Promise<any[]> =>
      apiClient.get("/tasks").then((res) => {
        const d = res.data;
        if (Array.isArray(d)) return d;
        if (d && Array.isArray(d.data)) return d.data;
        return [];
      }),

    get: (id: string): Promise<any> =>
      apiClient.get(`/tasks/${id}`).then((res) => res.data?.data ?? res.data),

    create: (data: any): Promise<any> =>
      apiClient.post("/tasks", data).then((res) => res.data),

    update: (id: string, data: any): Promise<any> =>
      apiClient.patch(`/tasks/${id}`, data).then((res) => res.data),

    delete: (id: string): Promise<void> =>
      apiClient.delete(`/tasks/${id}`).then((res) => res.data),

    addComment: (taskId: string, content: string): Promise<any> =>
      apiClient
        .post(`/tasks/${taskId}/comments`, { content })
        .then((res) => res.data),

    editComment: (
      taskId: string,
      commentId: string,
      content: string,
    ): Promise<any> =>
      apiClient
        .patch(`/tasks/${taskId}/comments/${commentId}`, { content })
        .then((res) => res.data),

    deleteComment: (taskId: string, commentId: string): Promise<void> =>
      apiClient
        .delete(`/tasks/${taskId}/comments/${commentId}`)
        .then((res) => res.data),
  },

  taskBadges: {
    list: (): Promise<any[]> =>
      apiClient.get("/task-badges").then((res) => res.data.data),

    create: (data: any): Promise<any> =>
      apiClient.post("/task-badges", data).then((res) => res.data.data),

    update: (id: string, data: any): Promise<any> =>
      apiClient.patch(`/task-badges/${id}`, data).then((res) => res.data.data),

    delete: (id: string): Promise<void> =>
      apiClient.delete(`/task-badges/${id}`).then((res) => res.data),
  },

  marketing: {
    notifications: {
      list: (userId: string, filters?: Record<string, any>): Promise<any> =>
        apiClient
          .get(`/marketing/${userId}/notifications`, { params: filters })
          .then((res) => res.data),

      summary: (userId: string): Promise<{ unread: number }> =>
        apiClient
          .get(`/marketing/${userId}/notifications/summary`)
          .then((res) => res.data?.data ?? res.data),

      markRead: (userId: string, id: string): Promise<any> =>
        apiClient
          .post(`/marketing/${userId}/notifications/${id}/read`)
          .then((res) => res.data),

      markAllRead: (userId: string): Promise<any> =>
        apiClient
          .post(`/marketing/${userId}/notifications/read-all`)
          .then((res) => res.data),
    },

    brandDefense: {
      get: (
        userId: string,
        params: { startDate: string; endDate: string },
      ): Promise<any> =>
        apiClient
          .get(`/marketing/${userId}/brand-defense`, { params })
          .then((res) => res.data?.data ?? res.data),
    },

    insights: {
      get: (userId: string): Promise<any> =>
        apiClient
          .get(`/marketing/${userId}/insights`)
          .then((res) => res.data?.data ?? res.data),
    },

    liveActivity: {
      get: (userId: string, since?: string): Promise<unknown> =>
        apiClient
          .get(`/marketing/${userId}/live-activity`, {
            params: since ? { since } : undefined,
          })
          .then((res) => res.data?.data ?? res.data),
    },

    marketDemandSeries: {
      get: (
        userId: string,
        params?: { startDate?: string; endDate?: string },
      ): Promise<unknown> =>
        apiClient
          .get(`/marketing/${userId}/market-demand-series`, {
            params: params || undefined,
          })
          .then((res) => res.data?.data ?? res.data),
    },

    trafficBreakdown: {
      get: (
        userId: string,
        params: { startDate: string; endDate: string; granularity?: string },
      ): Promise<any> =>
        apiClient
          .get(`/marketing/${userId}/traffic-breakdown`, { params })
          .then((res) => res.data?.data ?? res.data),
    },

    engagedSessions: {
      get: (userId: string) =>
        apiClient
          .get(`/marketing/${userId}/engaged-sessions`)
          .then((r) => r.data.data),
    },

    apiHealth: {
      get: (): Promise<{
        rateLimited: boolean;
        sources: string[];
        since: string | null;
      }> =>
        apiClient
          .get(`/marketing/api-health`)
          .then((r) => r.data?.data ?? r.data),
    },

    monthlyRevenue: {
      get: (
        userId: string,
        params: { startDate: string; endDate: string },
      ): Promise<any> =>
        apiClient
          .get(`/marketing/${userId}/monthly-revenue`, { params })
          .then((res) => res.data?.data ?? res.data),
    },

    digests: {
      list: (userId: string): Promise<unknown> =>
        apiClient
          .get(`/marketing/${userId}/digests`)
          .then((res) => res.data?.data ?? res.data),

      availableFields: (userId: string): Promise<unknown> =>
        apiClient
          .get(`/marketing/${userId}/digests/available-fields`)
          .then((res) => res.data?.data ?? res.data),

      create: (userId: string, body: Record<string, unknown>): Promise<unknown> =>
        apiClient
          .post(`/marketing/${userId}/digests`, body)
          .then((res) => res.data?.data ?? res.data),

      get: (userId: string, digestId: string): Promise<unknown> =>
        apiClient
          .get(`/marketing/${userId}/digests/${digestId}`)
          .then((res) => res.data?.data ?? res.data),

      update: (
        userId: string,
        digestId: string,
        body: Record<string, unknown>,
      ): Promise<unknown> =>
        apiClient
          .patch(`/marketing/${userId}/digests/${digestId}`, body)
          .then((res) => res.data?.data ?? res.data),

      pause: (userId: string, digestId: string): Promise<unknown> =>
        apiClient
          .post(`/marketing/${userId}/digests/${digestId}/pause`)
          .then((res) => res.data?.data ?? res.data),

      resume: (userId: string, digestId: string): Promise<unknown> =>
        apiClient
          .post(`/marketing/${userId}/digests/${digestId}/resume`)
          .then((res) => res.data?.data ?? res.data),

      delete: (userId: string, digestId: string): Promise<void> =>
        apiClient
          .delete(`/marketing/${userId}/digests/${digestId}`)
          .then(() => undefined),

      preview: (userId: string, digestId: string): Promise<unknown> =>
        apiClient
          .get(`/marketing/${userId}/digests/${digestId}/preview`)
          .then((res) => res.data?.data ?? res.data),

      sendNow: (
        userId: string,
        digestId: string,
        body?: { recipients?: string[] },
      ): Promise<unknown> =>
        apiClient
          .post(`/marketing/${userId}/digests/${digestId}/send-now`, body ?? {})
          .then((res) => res.data?.data ?? res.data),
    },
  },

  dashboardInsights: {
    get: (
      userId: string,
      params?: { refresh?: boolean },
    ): Promise<unknown> =>
      apiClient
        .get(`/dashboard-insights/${userId}`, {
          params: params?.refresh ? { refresh: "1" } : undefined,
        })
        .then((res) => res.data?.data ?? res.data),
  },

  supportTickets: {
    create: (data: {
      message: string;
      email?: string;
      screenshot?: string;
      conversationContext?: Array<{ role: string; content: string }>;
      source?: string;
      page?: string;
    }) => apiClient.post("/support-tickets", data).then((res) => res.data),
  },

  submitFeedback: (payload: SubmitFeedbackPayload) => {
    return apiClient.post("/feedback", payload).then((res) => res.data);
  },

  multiplierConfig: {
    get: (userId: string) =>
      apiClient
        .get(`/pricing/users/${userId}/multiplier-config`)
        .then((res) => res.data),

    update: (userId: string, body: Record<string, any>) =>
      apiClient
        .patch(`/pricing/users/${userId}/multiplier-config`, body)
        .then((res) => res.data),

    undo: (userId: string, changeId: string) =>
      apiClient
        .post(`/pricing/users/${userId}/multiplier-config/undo`, { changeId })
        .then((res) => res.data),

    reset: (
      userId: string,
      body: { resetBase?: boolean; deleteRules?: boolean; reasoning?: string; trigger?: string }
    ) =>
      apiClient
        .post(`/pricing/users/${userId}/multiplier-config/reset`, body)
        .then((res) => res.data),

    deleteRule: (userId: string, ruleId: string) =>
      apiClient
        .delete(`/pricing/users/${userId}/multiplier-config/rules/${ruleId}`)
        .then((res) => res.data),
  },

  onboardingAgentSearch: (data: { query: string }) =>
    apiClient
      .post("/onboarding-agent/search", data, { timeout: ONBOARDING_AGENT_TIMEOUT_MS })
      .then((res) => res.data),

  onboardingAgentEscalate: (data: { question: string; details?: string; snapshot: unknown }) =>
    apiClient
      .post("/onboarding-agent/escalation", data, { timeout: ONBOARDING_AGENT_TIMEOUT_MS })
      .then((res) => res.data),
};
